import { cn } from "@babybjorn/ui";
import type { StoryblokRichTextNode } from "@storyblok/react";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { motion } from "motion/react";
import {
	forwardRef,
	type CSSProperties,
	type HTMLAttributes,
	type ReactElement,
	type ReactNode,
} from "react";
import Link from "../../../../Link";
import type { LinkProps } from "../../../../Link/Link";
import RichText from "../../../../RichText";
import * as s from "./HotspotContent.module.scss";

type HotspotContentProps = HTMLAttributes<HTMLDivElement> & {
	headline: string;
	hotspotContent: string | StoryblokRichTextNode<ReactElement> | undefined;
	image?: ReactNode;
	tag?: string;
	link?: LinkProps;
	style?: CSSProperties;
	productGallery?: boolean;
};

const HotspotContent = forwardRef<HTMLDivElement, HotspotContentProps>(
	({ headline, hotspotContent, image, style, link, productGallery }, ref) => {
		const { t } = useTranslation();
		const goToLabel = t("Go to");
		return (
			<motion.div
				ref={ref}
				initial={{ opacity: 0, scale: 0.5 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.2 }}
				className={cn(s.container, {
					[s.onlyText]: !image && !headline,
					[s.productGalleryContainer]: productGallery,
					[s.noLink]: !link?.href,
					[s.link]: !!link?.href,
				})}
				style={style}
			>
				<Link
					href={link?.href}
					target={link?.target ?? "_self"}
					title={`${goToLabel} ${headline}`}
				>
					{image && <div className={s.image}>{image}</div>}

					<div
						className={cn(s.content, { [s.productGallery]: productGallery })}
					>
						{headline && <p className={s.headline}>{headline}</p>}
						{hotspotContent && (
							<RichText
								className={cn(s.text, { [s.center]: !image && !headline })}
								content={hotspotContent}
							/>
						)}
					</div>
				</Link>
			</motion.div>
		);
	},
);

HotspotContent.displayName = "HotspotContent";

export default HotspotContent;
